import { useRef } from "react";
import { Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { NavLang } from "../../components/app/headers/layout";
import ArticlePublicationCard from "../../components/cards/ArticlePublicationCard";
import Loader from "../../components/Loader";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useApiInfiniteQuery, useApiQuery } from "../../services/api";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";

const cardListClassName =
  "flex flex-col divide-y divide-ds-slate-100 *:py-4 [&>*:first-child]:pt-0";

export default function AuthorPage({ id }: { id: string }) {
  const { t } = useTranslation();
  const { activeLang, contentLang } = useActiveLang();
  const isDesktop = isDesktopLayout();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { savePageScrollY, restorePageScrollY } = usePageScroll(scrollRef);

  const authorQuery = useApiQuery("/webapp/public/authors/:id", {
    param: { id },
    durable: true,
  });
  const author = authorQuery.state === "success" ? authorQuery.body : null;

  const articlesQuery = useApiInfiniteQuery(
    "/webapp/public/authors/:id/articles",
    {
      param: { id },
      query: { limit: 20, language: contentLang },
    }
  );
  const articles = articlesQuery.state === "success" ? articlesQuery.docs : [];
  const publicationCount =
    articlesQuery.state === "success" && articlesQuery.total !== undefined
      ? articlesQuery.total
      : null;

  useEffectOnce(() => {
    if (articles.length > 0) restorePageScrollY();
  }, []);

  function handleScrollEnd() {
    if (articlesQuery.state === "success" && articlesQuery.canFetchNextPage) {
      articlesQuery.fetchNextPage();
    }
  }

  if (authorQuery.state === "error") return <Redirect to="/discovery" />;

  const isLoading = authorQuery.state === "pending" && authorQuery.isFetching;

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      headerClassName="bg-white/90 backdrop-blur-md"
      header={<ContentHeader transparent actions={<NavLang />} />}
      onScrollEnd={handleScrollEnd}
    >
      {isLoading || author === null ? (
        <Loader expanded />
      ) : (
        <div className={isDesktop ? "mx-auto w-full max-w-190 pb-20" : "pb-10"}>
          {/* Header block */}
          <div className="flex flex-col gap-2 px-5 pt-3">
            <h1 className="m-0 font-dm-sans text-2xl font-semibold tracking-tight text-ds-black">
              {author.displayName}
            </h1>
            <p className="m-0 text-footnote font-normal text-ds-slate-500">
              {publicationCount !== null && (
                <>
                  {`${publicationCount.toLocaleString(activeLang)} ${t("publications")}`}
                  {" · "}
                </>
              )}
              <a
                href={`https://orcid.org/${author.orcid}`}
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                ORCID {author.orcid}
              </a>
            </p>
          </div>

          {/* Publications */}
          <section className="mt-8 flex flex-col gap-3 px-5">
            <SectionTitle title={t("Publications")} />
            {articlesQuery.state === "pending" ? (
              <div className={cardListClassName}>
                {Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonPublicationCard
                    showImage
                    key={index}
                    showDescription
                  />
                ))}
              </div>
            ) : articles.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <p className="text-base font-semibold text-ds-slate-500">
                  {t("There are no publications for this author yet")}
                </p>
              </div>
            ) : (
              <div className={cardListClassName}>
                {articles.map((article) => (
                  <div key={article.id}>
                    <ArticlePublicationCard
                      roomId={null}
                      article={article}
                      analyticsListName={AnalyticsItemList.AUTHOR_PAGE}
                      recommendation={null}
                      onClick={savePageScrollY}
                      className={null}
                      showImage={true}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </ScrollableLayout>
  );
}
